import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Wrench, AlertTriangle, CheckCircle2, XCircle, HelpCircle, Copy } from 'lucide-react';
import { puntovService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import TablaDatos from './TablaDatos';
import Modal from './Modal';
import DialogoRepararMaquinas from './DialogoRepararMaquinas';

const sinRol = (b) => b.usuarios_sin_rol || [];

// Usuarios cuyo rol no existe: no pueden iniciar sesión en Punto V.
const textoSinRol = (g) => {
  const quienes = `${g.usuarios.length} usuario(s) (${g.usuarios.join(', ')})`;
  if (g.id_rol === null) return `${quienes} sin rol asignado: no pueden iniciar sesión`;
  return `${quienes} con el rol ${g.id_rol}${g.es_del_dueno ? ' del dueño' : ''}, que no existe: no pueden iniciar sesión`;
};

// Clasificación para mostrar (los datos los mide Punto V contra su esquema).
const estadoDe = (b) => {
  if (!b.existe) return 'sin_base';
  if (b.tablas_faltantes.length) return 'incompleta';
  if (b.maquinas_sin_tablas.length || b.maquinas_repetidas.length || b.nombres_parecidos.length || sinRol(b).length) return 'revisar';
  if (!b.usuarios) return 'sin_usuarios';
  return 'ok';
};

const ESTADOS = {
  ok: { etiqueta: 'Completa', clase: 'text-green-700 bg-green-50 border-green-200', icono: CheckCircle2 },
  incompleta: { etiqueta: 'Incompleta', clase: 'text-red-700 bg-red-50 border-red-200', icono: XCircle },
  revisar: { etiqueta: 'Revisar', clase: 'text-amber-700 bg-amber-50 border-amber-200', icono: AlertTriangle },
  sin_usuarios: { etiqueta: 'Sin usuarios', clase: 'text-gray-600 bg-gray-50 border-gray-200', icono: HelpCircle },
  sin_base: { etiqueta: 'Base no existe', clase: 'text-gray-600 bg-gray-100 border-gray-200', icono: XCircle }
};

const problemas = (b) => {
  const lista = [];
  if (!b.existe) lista.push(`Hay ${b.usuarios} usuario(s) apuntando a esta base, pero no existe en el servidor`);
  if (b.existe && b.tablas_faltantes.length) lista.push(`Faltan ${b.tablas_faltantes.length} de ${b.tablas_esperadas} tablas: ${b.tablas_faltantes.join(', ')}`);
  const porMayusculas = b.maquinas_sin_tablas.filter(m => m.tablas_parecidas.length);
  const sinTablas = b.maquinas_sin_tablas.filter(m => !m.tablas_parecidas.length);
  if (porMayusculas.length) {
    const ej = porMayusculas[0];
    lista.push(
      `${porMayusculas.length === 1 ? '1 máquina tiene' : `${porMayusculas.length} máquinas tienen`} sus tablas con otras mayúsculas `
      + `(${porMayusculas.map(m => m.id_maquina).join(', ')}). Ej.: se espera ${ej.tablas_faltantes[0]} y existe ${ej.tablas_parecidas[0]}`
    );
  }
  if (sinTablas.length) {
    lista.push(`${sinTablas.length === 1 ? '1 máquina no tiene' : `${sinTablas.length} máquinas no tienen`} sus tablas: ${sinTablas.map(m => m.id_maquina).join(', ')}`);
  }
  if (b.maquinas_repetidas.length) lista.push(`Máquinas registradas más de una vez: ${b.maquinas_repetidas.join(', ')}`);
  if (b.nombres_parecidos.length) lista.push(`Mismo nombre con otras mayúsculas: ${b.nombres_parecidos.join(', ')} (para MySQL son bases distintas)`);
  sinRol(b).forEach(g => lista.push(textoSinRol(g)));
  if (b.existe && !b.usuarios) lista.push('Ningún usuario apunta a esta base');
  return lista;
};

const conProblemasDeMaquinas = (b) => b.existe && (b.maquinas_sin_tablas.length > 0 || b.maquinas_repetidas.length > 0);

const claveBase = (b) => b.base;
const textoBase = (b) => `${b.base} ${problemas(b).join(' ')}`;

const DiagnosticoBases = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [porReparar, setPorReparar] = useState(null);
  const [reparando, setReparando] = useState(false);
  const [errorReparar, setErrorReparar] = useState('');
  const [maquinasDe, setMaquinasDe] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await puntovService.diagnosticoBases();
      setBases(res.data?.bases || []);
    } catch (err) {
      setError(err.message || 'No se pudo diagnosticar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const conteo = useMemo(() => {
    const c = { ok: 0, incompleta: 0, revisar: 0, sin_usuarios: 0, sin_base: 0 };
    bases.forEach(b => { c[estadoDe(b)] += 1; });
    return c;
  }, [bases]);
  const enRiesgo = useMemo(() => bases.filter(b => b.cobro_activo && ['incompleta', 'revisar', 'sin_base'].includes(estadoDe(b))), [bases]);

  const reparar = async () => {
    setErrorReparar('');
    setReparando(true);
    try {
      const res = await puntovService.repararBase(porReparar.base);
      const faltan = res.data?.base?.tablas_faltantes?.length ?? 0;
      const roles = res.data?.roles_reparados || [];
      const estructura = faltan === 0
        ? `estructura completa (${porReparar.tablas_faltantes.length} tabla(s) creada(s))`
        : `se reparó, pero todavía faltan ${faltan} tabla(s)`;
      setAviso(`${porReparar.base}: ${estructura}.${roles.length ? ` Roles: ${roles.join('. ')}.` : ''}`);
      setPorReparar(null);
      await cargar();
    } catch (err) {
      setErrorReparar(`${err.message || 'No se pudo reparar'}${typeof err.salida === 'string' && err.salida ? `\n${err.salida}` : ''}`);
    } finally {
      setReparando(false);
    }
  };

  const columnas = useMemo(() => [
    {
      id: 'base', titulo: 'Base', ordenable: true, valor: (b) => b.base,
      render: (b) => (
        <>
          <div className="font-mono text-xs text-gray-800">{b.base}</div>
          <div className="text-xs text-gray-400">{b.usuarios} usuario(s){b.tipo === 'interna' ? ' · interna' : ''}</div>
        </>
      )
    },
    {
      id: 'estado', titulo: 'Estado', ordenable: true, valor: (b) => estadoDe(b),
      filtro: Object.entries(ESTADOS).map(([valor, e]) => ({ valor, etiqueta: e.etiqueta })),
      render: (b) => {
        const e = ESTADOS[estadoDe(b)];
        return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${e.clase}`}><e.icono size={12} /> {e.etiqueta}</span>;
      }
    },
    {
      id: 'estructura', titulo: 'Tablas', alinear: 'center', ordenable: true,
      valor: (b) => (b.existe ? b.tablas_esperadas - b.tablas_faltantes.length : -1),
      render: (b) => (b.existe
        ? <span className={`tabular-nums ${b.tablas_faltantes.length ? 'text-red-600 font-medium' : 'text-gray-700'}`}>{b.tablas_esperadas - b.tablas_faltantes.length}/{b.tablas_esperadas}</span>
        : <span className="text-gray-400">—</span>)
    },
    { id: 'maquinas', titulo: 'Máquinas', alinear: 'center', ordenable: true, valor: (b) => b.maquinas, render: (b) => <span className="tabular-nums">{b.existe ? b.maquinas : '—'}</span> },
    {
      id: 'problemas', titulo: 'Problemas', valor: (b) => problemas(b).length,
      render: (b) => {
        const lista = problemas(b);
        if (!lista.length) return <span className="text-gray-400">—</span>;
        return (
          <ul className="text-xs text-gray-600 space-y-0.5 max-w-xl">
            {b.cobro_activo && estadoDe(b) !== 'ok' && estadoDe(b) !== 'sin_usuarios' && (
              <li className="text-red-700 font-medium">Tiene cobro activo: su mensualidad puede fallar</li>
            )}
            {lista.map((p, i) => <li key={i} className="break-words">{p}</li>)}
          </ul>
        );
      }
    },
    ...(esAdmin ? [{
      id: 'acciones', titulo: '', alinear: 'right', valor: () => '',
      render: (b) => (
        <div className="flex flex-col items-end gap-1">
          {b.existe && (b.tablas_faltantes.length > 0 || sinRol(b).length > 0) && (
            <button
              type="button"
              onClick={() => { setAviso(''); setErrorReparar(''); setPorReparar(b); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 whitespace-nowrap"
            >
              <Wrench size={12} /> Reparar
            </button>
          )}
          {conProblemasDeMaquinas(b) && (
            <button
              type="button"
              onClick={() => { setAviso(''); setMaquinasDe(b.base); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 whitespace-nowrap"
            >
              <Wrench size={12} /> Reparar máquinas
            </button>
          )}
        </div>
      )
    }] : [])
  ], [esAdmin]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Cada base de Punto V comparada con su esquema oficial: tablas, máquinas y nombres.
        </p>
        <button onClick={cargar} disabled={loading} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60 self-start sm:self-auto">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Diagnosticar de nuevo
        </button>
      </div>

      {!loading && bases.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
          {Object.entries(ESTADOS).map(([clave, e]) => (
            <div key={clave} className={`rounded-xl border px-3 py-2 ${e.clase}`}>
              <div className="flex items-center gap-1.5 text-xs font-medium"><e.icono size={13} /> {e.etiqueta}</div>
              <div className="text-xl font-bold tabular-nums">{conteo[clave]}</div>
            </div>
          ))}
        </div>
      )}

      {enRiesgo.length > 0 && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <strong>{enRiesgo.length === 1 ? '1 cliente con cobro activo' : `${enRiesgo.length} clientes con cobro activo`}</strong> tiene(n) la base con problemas, y su mensualidad puede fallar el día de cobro:
            {' '}{enRiesgo.map(b => b.base.replace(/^esgamanc_/, '')).join(', ')}.
          </span>
        </div>
      )}
      {aviso && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> <span>{aviso}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && bases.length === 0 ? (
          <div className="text-center py-12"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <TablaDatos columnas={columnas} filas={bases} clave={claveBase} textoBusqueda={textoBase} placeholderBusqueda="Buscar base o problema" vacio="Sin bases" />
        )}
      </div>

      <p className="text-xs text-gray-400 flex items-start gap-1.5">
        <Copy size={12} className="mt-0.5 flex-shrink-0" />
        Los problemas de máquinas se reparan con «Reparar máquinas», eligiendo qué aplicar en cada una. Las filas repetidas con datos
        distintos y los nombres de base parecidos no se arreglan solos: hay que decidir cuál vale y corregirlo en Punto V con respaldo.
      </p>

      {maquinasDe && (
        <DialogoRepararMaquinas base={maquinasDe} onClose={() => setMaquinasDe(null)} onReparado={cargar} />
      )}

      {porReparar && (
        <Modal
          titulo={`Reparar ${porReparar.base}`}
          subtitulo={porReparar.tablas_faltantes.length ? "Crear las tablas que faltan" : "Recuperar roles perdidos"}
          icono={Wrench}
          onClose={() => setPorReparar(null)}
          bloqueado={reparando}
          ancho="md"
          pie={(
            <>
              <button type="button" onClick={() => setPorReparar(null)} disabled={reparando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">Cancelar</button>
              <button type="button" onClick={reparar} disabled={reparando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                <Wrench size={15} /> {reparando ? 'Reparando…' : sinRol(porReparar).length && !porReparar.tablas_faltantes.length ? 'Reparar roles' : 'Reparar'}
              </button>
            </>
          )}
        >
          <div className="space-y-3 text-sm text-gray-700">
            {porReparar.tablas_faltantes.length > 0 && (
              <>
                <p>Se ejecuta <span className="font-mono text-xs">tenant:provision</span> sobre esta base: crea las {porReparar.tablas_faltantes.length} tabla(s) que faltan, vacías, y completa columnas e índices.</p>
                <ul className="text-xs font-mono bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto">
                  {porReparar.tablas_faltantes.map(t => <li key={t}>{t}</li>)}
                </ul>
                <p className="text-xs text-gray-500">No borra ni modifica datos, ni crea tablas de máquinas. Es la misma operación que Punto V corre en cada despliegue.</p>
              </>
            )}
            {sinRol(porReparar).length > 0 && (
              <>
                <p>Hay usuarios cuyo rol no existe, y por eso no pueden iniciar sesión:</p>
                <ul className="text-xs bg-gray-50 rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                  {sinRol(porReparar).map(g => <li key={g.id_rol ?? 'sin-rol'}>{textoSinRol(g)}</li>)}
                </ul>
                <p className="text-xs text-gray-500">
                  El rol del dueño de la empresa se recrea como Administrador, con todos sus permisos y máquinas.
                  Cualquier otro rol perdido se recrea con su mismo número pero sin permisos: esos usuarios podrán entrar,
                  y el administrador les asigna los permisos desde Usuarios. Nadie gana permisos que no tenía.
                </p>
              </>
            )}
            {errorReparar && <div className="bg-red-50 whitespace-pre-wrap border border-red-200 text-red-700 rounded-lg p-3 text-sm">{errorReparar}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DiagnosticoBases;
